<template>
    <section class="get-inspired-section">
        <div class="container">
            <div class="section-header">
                <div class="header-left">
                    <h2 class="section-title">Get Inspired</h2>
                    <p class="section-subtitle">Latest Ideas & Trends & Info</p>
                </div>
                <div class="header-right">
                    <nuxt-link :to="getInspiredCategory?.fullUrl || '/get-inspired.html'" class="view-all-link">
                        VIEW ALL
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 200 200">
                            <path fill="currentColor" d="M151.6,51.7l0,98.1c0,0.9-0.7,1.6-1.6,1.6c-0.4,0-0.8-0.2-1.1-0.5L147,149c-0.6-0.6-0.9-1.4-0.9-2.2l0-86.8l-90.5,90.6c-0.6,0.6-1.6,0.6-2.2,0l-2.1-2.1c-0.6-0.6-0.6-1.6,0-2.2l90.6-90.6l-86.8,0c-0.8,0-1.6-0.3-2.2-0.9l-1.9-1.9c-0.6-0.6-0.6-1.6,0-2.2c0.3-0.3,0.7-0.5,1.1-0.5l98.1,0C150.9,50.2,151.6,50.9,151.6,51.7L151.6,51.7z" />
                        </svg>
                    </nuxt-link>
                </div>
            </div>

            <div class="articles-slider">
                <button class="nav-btn nav-prev" aria-label="Previous">
                    <ArrowLeftIcon />
                </button>

      

                <Swiper
                    :modules="swiperModules"
                    :slides-per-view="'auto'"
                    :autoplay="{
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }" 
                    :navigation="{
                        prevEl: '.nav-prev',
                        nextEl: '.nav-next',
                    }"
                    @swiper="onSwiperInit"
                    class="articles-swiper"
                    :grab-cursor="true"
                >
                    <SwiperSlide v-for="(article, index) in articleList" :key="article.id" class="!w-auto">
                        <div class="article-card-div"
                            :ref="el => setCardRef(el, index)">
                            <nuxt-link
                                class="article-card"  
                                :to="article.fullUrl" >
                                <div class="card-image">
                                    <img :src="article.imageUrl" :alt="article.imageAlt || article.title" loading="lazy" />
                                </div>
                                <div class="card-content">
                                    <div class="card-text">
                                        <h3 class="card-title">{{ article.title }}</h3>
                                        <p class="card-description">{{ article.description }}</p>
                                    </div>
                                    <div class="card-footer">
                                        <span class="card-date">{{ article.updateTime?.slice(0, 10) }}</span>
                                        <span class="card-more">Read More</span>
                                    </div>
                                </div>
                            </nuxt-link>
                        </div>
                    </SwiperSlide>
                </Swiper>

                <button class="nav-btn nav-next" aria-label="Next">
                    <ArrowRightIcon />
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import cmsUtils from '~/utils/cmsUtils';
    import type { Category, Article, Page } from '~/types';
    import { ref, onMounted, onUnmounted, nextTick } from 'vue';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { Navigation, Autoplay } from 'swiper/modules';

    import type { Swiper as SwiperType } from 'swiper';
    import ArrowLeftIcon from '../icons/ArrowLeftIcon.vue';
    import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
    import { useHttpBy$Fetch } from '~/utils/httpUtil'

    const swiperModules = [Navigation, Autoplay];
    const swiperInstance = ref<SwiperType | null>(null);
    const articleList = ref<Article[]>([]);
    const getInspiredCategory = ref<Category | null>(null);
    const visibleCards = ref(new Set<number>());
    const cardRefs = new Map<number, HTMLElement>();
    const observer = ref<IntersectionObserver | null>(null);

    const cmsInfo = await cmsUtils.getCmsInfo();
    getInspiredCategory.value = cmsUtils.getCategoryByUrl('get-inspired', cmsInfo);

    if (getInspiredCategory.value) {
        const childCategoryIds = cmsUtils.getChildCategoryIds(getInspiredCategory.value.id, cmsInfo);
        const allCategoryIds = [getInspiredCategory.value.id, ...childCategoryIds];

        const body = {
            "websiteId": cmsInfo!.website.id,
            "categoryIds": allCategoryIds,
            "orderType": "top",
            "pageNum": 1,
            "pageSize": 8
        };

        const articlesPage = await useHttpBy$Fetch("/nuxt/article/list", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: body
        }) as Page<Article>;

        articlesPage.rows.forEach((article: Article) => {
            article.fullUrl = cmsUtils.appendCategoryUrl(getInspiredCategory.value!, cmsInfo, "/" + article.titleUrl);
        });

        articleList.value = articlesPage.rows;
    }

    const onSwiperInit = (swiper: SwiperType) => {
        swiperInstance.value = swiper;
    };

    function setCardRef(el: Element | ComponentPublicInstance | null, index: number) {
        if (el && el instanceof HTMLElement) {
            cardRefs.set(index, el);
        } else {
            cardRefs.delete(index);
        }
    }

    function createObserver() {
        observer.value = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const cardId = entry.target.getAttribute('data-card-id');
                if (cardId !== null) {
                    const index = parseInt(cardId);
                    if (entry.isIntersecting) {
                        visibleCards.value.add(index);
                    }
                }
            });
        }, {
            root: null,
            rootMargin: '0px 0px -100px 0px',
            threshold: 0.1
        });
    }

    function observeCards() {
        if (!observer.value) return;
        cardRefs.forEach((el, index) => {
            el.setAttribute('data-card-id', index.toString());
            observer.value!.observe(el);
        });
    }

    onMounted(() => {
        nextTick(() => {
            setTimeout(() => {
                createObserver();
                observeCards();
            }, 300);
        });
    });

    onUnmounted(() => {
        if (observer.value) {
            observer.value.disconnect();
            observer.value = null;
        }
        cardRefs.clear();
    });
</script>

<style scoped lang="scss">
    .get-inspired-section {
        padding: 60px 0;
        background-color: var(--gray-100);
    }

    .container {
        margin: 0 auto;
        padding: 0 0.75rem 4rem;
    }

    .section-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2rem;

        @media (min-width: 768px) {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
        }
    }

    .header-right {
        @media (max-width: 767px) {
            display: none;
        }
    }

    .section-title {
        font-size: 1.75rem;
        font-weight: 600;
    }

    .section-subtitle {
        font-size: 1.25rem;
        font-weight: 400;
        margin-top: -0.375rem;
    }

    .view-all-link {
        display: flex;
        align-items: center;
        color: var(--green-600);
        font-weight: 700;
        text-decoration: none;
        gap: 0.5rem;
        margin-top: 1rem;

        &:hover {
            color: var(--green-700);
        }

        svg {
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    .articles-slider {
        position: relative;
        margin-top: 2rem;
    }

    .nav-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
        width: 2.5rem;
        height: 2.5rem;
        background: var(--white);
        border: 1px solid var(--gray-200);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        color: var(--green-600);

        svg {
            width: 1rem;
            height: 1rem;
            stroke: var(--green-600);
        }

        &:hover:not(:disabled) {
            background: var(--green-600);
            color: var(--white);
            border-color: var(--green-600);

            svg {
                stroke: var(--white);
            }
        }

        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }

    .nav-prev {
        left: 0.75rem;
    }

    .nav-next {
        right: 0.75rem;
    }

    .articles-swiper {
        overflow: hidden;

        :deep(.swiper-wrapper) {
            display: flex;
        }
    }

    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
        display: none;
    }

    .article-card-div{
        display: flex;
        flex-direction: column;
        width: 380px;
        color: inherit;
        transition: box-shadow 0.3s ease;
        padding-right:16px;
    }

    .article-card {
        background-color: var(--white);
        border-radius: 0.625rem;
        overflow: hidden;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        text-decoration: none;
        &:hover {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
        }
    }

    .card-image {
        position: relative;
        width: 100%;
        height: 13.75rem;
    }

    .card-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .card-content {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    .card-text {
        flex-grow: 1;
    }

    .card-title {
        margin-bottom: 0.5rem;
        font-size: 1.25rem;
        line-height: 1.3;
        font-weight: 600;
        color: var(--gray-800);
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        line-clamp: 2;
        min-height:52px;
        &:hover {
            color: var(--green-600);
        }
    }

    .card-description {
        margin-top: 0.5rem;
        margin-bottom: 1rem;
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--gray-600);
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        line-clamp: 3;
        min-height:64px;
    }

    .card-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 1rem;
        margin-top: auto;
        border-top: 1px solid var(--gray-100);
    }

    .card-date {
        font-size: 0.75rem;
        color: var(--gray-500);
    }

    .card-more {
        font-size: 0.875rem;
        color: var(--green-600);
        font-weight: 400;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    .article-card.animate-fade-up {
        animation: fadeUp 0.8s ease-out forwards;
        opacity: 0;
        transform: translateY(20px);
    }

    @keyframes fadeUp {
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: 1024px) {
        .article-card {
            width: 300px;
        }
    }

    @media (max-width: 640px) {
        .article-card {
            width: 280px;
        }

        .articles-swiper {
            padding: 0 0 0.625rem;
        }

        .nav-btn {
            width: 2.25rem;
            height: 2.25rem;
        }
    }
</style>
